import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { RouteComponentProps, withRouter } from 'react-router-dom';

import { Button, Popper, Spinner, WorkArea } from '@xsolla/ui-core';
import { useModal } from '@xsolla/ui-modal';

import { getMailchimp } from '../../../api';
import { MailchimpStatus } from '../../../api/users/mailchimp/types';
import { RouteParams } from '../../../routes/types';
import ConnectModal from './ConnectModal';
import DisconnectModal from './DisconnectModal';
import MergeTagsModal from './MergeTagsModal';
import {
  MailchimpDescription,
  MailchimpPill,
  MailchimpPillTitle,
  MailchimpTitle,
  MailchimpWrapper,
  WrappedMailchimpIcon
} from './style';
import SuccessModal from './SuccessModal';

const MailchimpButton = ({ match }: RouteComponentProps): JSX.Element => {
  const intl = useIntl();
  const { projectId } = match.params as RouteParams;

  const [mailchimpStatus, setMailchimpStatus] = useState<MailchimpStatus>('never used');
  const [mailchimpListId, setMailchimpListId] = useState<string>('');
  const [fetchingStatus, setFetchingStatus] = useState<boolean>(true);
  const [errorCode, setErrorCode] = useState<string>('');

  const [onOpenConnectModal, onCloseConnectModal] = useModal(
    () => (
      <ConnectModal
        onClose={onCloseConnectModal}
        onSubmit={onConnect}
        projectId={projectId}
        setMailchimpListId={setMailchimpListId}
      />
    ),
    []
  );

  // const [onOpenConnectModal, onCloseConnectModal] = useModal(
  //   () => (
  //     <MergeTagsModal
  //       onClose={onCloseConnectModal}
  //       onSubmit={onMergeTags}
  //       projectId={projectId}
  //       mailchimpListId={mailchimpListId}
  //     />
  //   ),
  //   []
  // );

  const [onOpenMergeTagsModal, onCloseMergeTagsModal] = useModal(
    () => (
      <MergeTagsModal
        onClose={onCloseMergeTagsModal}
        onSubmit={onMergeTags}
        projectId={projectId}
        mailchimpListId={mailchimpListId}
      />
    ),
    [mailchimpListId]
  );

  const [onOpenDisconnectModal, onCloseDisconnectModal] = useModal(
    () => (
      <DisconnectModal
        onClose={onCloseDisconnectModal}
        onSubmit={onDisconnect}
        projectId={projectId}
      />
    ),
    []
  );

  const [onOpenSuccessModal, onCloseSuccessModal] = useModal(
    () => <SuccessModal onClose={onCloseSuccessModal} />,
    []
  );

  const onDisconnect = () => {
    setMailchimpStatus('never used');
    onCloseDisconnectModal();
  };

  const onConnect = () => {
    setMailchimpStatus('finished');
    onOpenMergeTagsModal();
    onCloseConnectModal();
  };

  const onMergeTags = () => {
    onOpenSuccessModal();
    onCloseConnectModal();
    onCloseMergeTagsModal();
  };

  useEffect(() => {
    getMailchimp(projectId)
      .then((res) => {
        setMailchimpStatus(res.export_status);
        setMailchimpListId(res.list_id);
      })
      .catch((err) => {
        if (err.code === '002-048' || err.code === '002-047') {
          setErrorCode(err.code);
          setMailchimpStatus('failed');
          setMailchimpListId('');
          return;
        }
        setMailchimpStatus('never used');
      })
      .finally(() => setFetchingStatus(false));
  }, []);

  const mailchimpPalette = useMemo(() => {
    switch (mailchimpStatus) {
      case 'finished':
        return 'action';
      case 'pending':
        return 'warning';
      case 'failed':
        return 'danger';
      default:
        return 'muted';
    }
  }, [mailchimpStatus]);

  const MailchimpConnectItem = useMemo(() => {
    return (
      <WorkArea>
        <MailchimpWrapper data-test-id="mailchimp-dropdown-wrapper">
          <MailchimpTitle>
            {intl.messages['users.mailchimp.button-dropdown.integration.title']}
          </MailchimpTitle>
          <MailchimpDescription>
            {intl.messages['users.mailchimp.button-dropdown.integration.description']}
          </MailchimpDescription>
          <Button
            look="default"
            palette="brand"
            size="sm"
            onClick={onOpenConnectModal}
            data-test-id="mailchimp-connect-button">
            {intl.messages['users.mailchimp.button-dropdown.integration.button']}
          </Button>
        </MailchimpWrapper>
      </WorkArea>
    );
  }, []);

  const MailchimpDisconnectItem = useMemo(() => {
    return (
      <WorkArea>
        <MailchimpWrapper>
          <MailchimpTitle>
            {intl.messages['users.mailchimp.button-dropdown.export-completed.title']}
          </MailchimpTitle>
          <MailchimpDescription>
            {intl.messages['users.mailchimp.button-dropdown.export-completed.description']}
          </MailchimpDescription>
          <Button look="tertiary" palette="muted" size="sm" onClick={onOpenDisconnectModal}>
            {intl.messages['users.mailchimp.button-dropdown.export-completed.button']}
          </Button>
          <div style={{ marginTop: '10px' }}>
            <Button look="tertiary" palette="brand" size="sm" onClick={onOpenMergeTagsModal}>
              {intl.messages['users.mailchimp.merge-tags-form.edit-tag']}
            </Button>
          </div>
        </MailchimpWrapper>
      </WorkArea>
    );
  }, []);

  const MailchimpErrorItem = useMemo(() => {
    return (
      <WorkArea>
        <MailchimpWrapper>
          {errorCode === '002-048' || errorCode === '002-047' ? (
            <MailchimpTitle>{intl.messages['app.error-title.mailchimp-not-found']}</MailchimpTitle>
          ) : (
            <MailchimpTitle>{intl.messages['app.error.title']}</MailchimpTitle>
          )}
          <MailchimpDescription>{intl.messages[`app.error.${errorCode}`]}</MailchimpDescription>
          <Button look="tertiary" palette="muted" size="sm" onClick={onOpenDisconnectModal}>
            {intl.messages['users.mailchimp.button-dropdown.export-completed.button']}
          </Button>
        </MailchimpWrapper>
      </WorkArea>
    );
  }, [errorCode]);

  const itemDisplayed = useMemo(() => {
    switch (mailchimpStatus) {
      case 'never used':
        return MailchimpConnectItem;
        break;
      case 'failed':
        return MailchimpErrorItem;
        break;
      default:
        return MailchimpDisconnectItem;
    }
  }, [mailchimpStatus]);

  return (
    <Popper placement="bottom-end" entry={itemDisplayed}>
      {(referenceProps) => (
        <MailchimpPill {...referenceProps} palette={mailchimpPalette} data-test-id="mailchimp-btn">
          {fetchingStatus ? (
            <Spinner />
          ) : (
            <>
              <WrappedMailchimpIcon />
              <MailchimpPillTitle>{intl.messages['users.mailchimp']}</MailchimpPillTitle>
            </>
          )}
        </MailchimpPill>
      )}
    </Popper>
  );
};

export default withRouter(MailchimpButton);
